const tariff_rates_url = process.env.VUE_APP_RATES_URL
    || process.env.VUE_APP_TARIFF_RATES_URL?.replace(/tariff-rates\/?$/i, "rates/")
    || "";

export const tariff_rates_endpoint = ensure_trailing_slash(tariff_rates_url);

export function ensure_trailing_slash(url) {
    if (!url) {
        return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
}

export function get_positive_integer_id(value) {
    const numeric_value = Number(value);

    return Number.isInteger(numeric_value) && numeric_value > 0 ? String(numeric_value) : "";
}

export function safe_parse_json(value, fallback_value = null) {
    if (value === null || value === undefined || value === "") {
        return fallback_value;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return fallback_value;
    }

    return JSON.parse(value);
}

export function safe_parse_maybe_double_encoded_json(value, fallback_value = null) {
    if (value === null || value === undefined || value === "") {
        return fallback_value;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return fallback_value;
    }

    try {
        let parsed_value = JSON.parse(value);

        if (typeof parsed_value === "string") {
            parsed_value = JSON.parse(parsed_value);
        }

        return parsed_value;
    } catch (error) {
        return fallback_value;
    }
}

export function format_api_errors(error_payload) {
    if (!error_payload) {
        return "Something went wrong while saving the tariff rate.";
    }

    const normalized_payload = error_payload.errors || error_payload.detail || error_payload.error || error_payload;

    if (typeof normalized_payload === "string") {
        return normalized_payload;
    }

    if (Array.isArray(normalized_payload)) {
        return normalized_payload.join(", ");
    }

    if (typeof normalized_payload === "object") {
        return Object.entries(normalized_payload)
            .map(([field, messages]) => {
                const message_text = Array.isArray(messages) ? messages.join(", ") : messages;
                return `${field}: ${message_text}`;
            })
            .join(" | ");
    }

    return "Something went wrong while saving the tariff rate.";
}

function normalize_array_rate_record(rate) {
    if (!Array.isArray(rate)) {
        return null;
    }

    return ({
        id: rate[0],
        name: rate[1],
        full_name: rate[2],
        code: rate[3],
        authority: rate[4],
        rates: rate[5],
        metadata: rate[6],
        effective_from: rate[7],
        is_active: rate[8],
        created_at: rate[9],
        updated_at: rate[10],
        tariff_book_id: rate[11]
    });
}

function is_empty_relation_value(value) {
    if (value === null || value === undefined || value === "") {
        return true;
    }

    return false;
}

function get_relation_object_id(value) {
    const relation_id = value.id;

    return relation_id === null || relation_id === undefined ? value.pk : relation_id;
}

function resolve_relation_id(value) {
    if (is_empty_relation_value(value)) {
        return null;
    }

    if (typeof value === "object") {
        return get_relation_object_id(value);
    }

    return value;
}

export function extract_rate_records(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.results)) {
        return payload.results;
    }

    return [];
}

export function extract_single_rate_record(payload) {
    if (!payload) {
        return null;
    }

    if (Array.isArray(payload?.data) && payload.data.length) {
        return payload.data[0];
    }

    if (Array.isArray(payload?.results) && payload.results.length) {
        return payload.results[0];
    }

    return payload;
}

export function normalize_rate_record(rate) {
    const normalized_source = normalize_array_rate_record(rate) || rate || {};

    const rates_payload = safe_parse_maybe_double_encoded_json(
        normalized_source?.rates_json ||
        normalized_source?.charge_components ||
        normalized_source?.rates ||
        normalized_source?.rate_values ||
        normalized_source?.rate_data ||
        normalized_source?.charges ||
        normalized_source?.tariff_rates,
        {}
    ) || {};

    const metadata_payload = safe_parse_maybe_double_encoded_json(
        normalized_source?.metadata_json ||
        normalized_source?.extra_metadata ||
        normalized_source?.metadata ||
        normalized_source?.matrix_metadata ||
        normalized_source?.extra_data ||
        normalized_source?.details,
        {}
    ) || {};

    return ({
        id: normalized_source?.id,
        tariff_id: resolve_relation_id(normalized_source?.tariff_id ?? normalized_source?.tariff),
        name: normalized_source?.name || normalized_source?.tariff_name || "Unnamed tariff rate",
        full_name: normalized_source?.full_name || normalized_source?.display_name || normalized_source?.description || "",
        code: normalized_source?.code || "",
        matrix_code: normalized_source?.matrix_code || metadata_payload["Matrix Code"] || "",
        authority: normalized_source?.local_authority || normalized_source?.authority || normalized_source?.local_authority_name || normalized_source?.category || "",
        effective_from: normalized_source?.effective_from || normalized_source?.start_date || "",
        effective_to: normalized_source?.effective_to || normalized_source?.end_date || "",
        tariff_category: normalized_source?.tariff_category || normalized_source?.category || "",
        is_active: normalized_source?.is_active !== undefined ? Boolean(normalized_source.is_active) : true,
        created_at: normalized_source?.created_at || "",
        updated_at: normalized_source?.updated_at || "",
        tariff_book_id: resolve_relation_id(normalized_source?.tariff_book_id ?? normalized_source?.tariff_book),
        rates: rates_payload,
        metadata: metadata_payload,
        raw: normalized_source
    });
}

export function normalize_match_value(value) {
    return String(value || "").trim().toLowerCase();
}
